<template>
  <div class="dashboard">
    <div class="container">

      <div style="margin: 0 0 1rem;">
        <router-link to="/dashboard" class="alt-link">&lt; Back to Dashboard</router-link>
      </div>

      <div v-if="!this.$store.state.loadingSpinner">

        <div class="sub">{{ auction.address }}</div>
        <h2>Bidders</h2>

        <div class="auction-container" v-if="auction.id">
          <div class="auction-details">
            <div class="auction-photo" :style="'background-image: url(' + propertyPhoto + ')'" />
            <div><strong>{{ auction.address }}</strong></div>
            <div>{{ auction.zip }} {{ auction.city }}, {{ auction.state }}</div>
            <div>APN: {{ auction.metadata.apn }}</div>
            <div class="margin-top"><AuctionStatusIndicator :auction="auction" /></div>
          </div>
          <div class="auction-reg">
            <div class="sub">Registration Period</div>
            <div>{{ auction.publishDate | prettyDateTime(auction.metadata.timezone) }}</div>
            <div>{{ auction.freezeDate | prettyDateTime(auction.metadata.timezone) }}</div>
          </div>
          <div class="auction-times">
            <div class="sub">Auction Period</div>
            <div>{{ auction.startDate | prettyDateTime(auction.metadata.timezone) }}</div>
            <div>{{ auction.endDate | prettyDateTime(auction.metadata.timezone) }}</div>
          </div>
        </div>

        <div class="align-right" v-if="auction.status === auctionStatuses.PROCESSING.value">
          <button class="btn-neutral-light" @click="$router.push('/closing/' + auctionId)">New Purchase Agreement</button>
        </div>

        <v-data-table :headers="headers" :pagination.sync="pagination" :items="participants" must-sort hide-actions item-key="userId" :loading="loadingCounter > 0">
          <template slot="no-data">
            No bidders
          </template>
          <template slot="items" slot-scope="props">
            <tr @click="clickBidder(props.item)" class="pointer">
              <td class="nowrap">
                <img :src="props.item.profile.photoUrl || require('../assets/placeholder-user.png')" class="user-avatar" />
                <div style="font-size: 14px;">{{ props.item.profile.firstName }} {{ props.item.profile.lastName }}</div>
                <div style="color: #777; font-size: 12px;">{{ props.item.profile.alias }}</div>
              </td>
              <td>
                <template v-if="props.item.agencyRelationship === agencyRelationships.SELLING_AGENT.value">
                  <div v-if="props.item.sellingAgentProfile.id">
                    <div>{{ props.item.sellingAgentProfile.firstName }} {{ props.item.sellingAgentProfile.lastName }}</div>
                    <div style="font-size: 12px;"><a class="alt-link" @click.stop="" :href="'mailto:' + props.item.sellingAgentProfile.email">{{ props.item.sellingAgentProfile.email }}</a></div>
                  </div>
                  <div v-else>
                    <div>(Selling Agent Invited)</div>
                    <div style="font-size: 12px;"><a class="alt-link" @click.stop="" :href="'mailto:' + props.item.sellingAgentEmail">{{ props.item.sellingAgentEmail }}</a></div>
                  </div>
                </template>
                <template v-else-if="props.item.agencyRelationship === agencyRelationships.LISTING_AGENT.value">
                  <div>{{ listingAgentProfile.firstName }} {{ listingAgentProfile.lastName }}</div>
                  <div style="font-size: 12px;"><a class="alt-link" @click.stop="" :href="'mailto:' + listingAgentProfile.email">{{ listingAgentProfile.email }}</a></div>
                </template>
                <template v-else-if="props.item.agencyRelationship === agencyRelationships.MYSELF.value">
                  <div>{{ props.item.profile.firstName }} {{ props.item.profile.lastName }}</div>
                  <div style="font-size: 12px;"><a class="alt-link" @click.stop="" :href="'mailto:' + props.item.profile.email">{{ props.item.profile.email }}</a></div>
                </template>
              </td>
              <td>
                {{ props.item.id }}
              </td>
              <td style="font-size: 10px;">
                {{ props.item.created | prettyDateTimeShort }}
              </td>
              <td>
                <div class="sub">Highest Bid</div>
                <div>{{ myHighestBid(props.item.userId) | prettyMoney }}</div>
              </td>
              <td>
                <AuctionRoleStatusIndicator :auction="props.item" />
                <div style="color: #777; font-size: 10px; margin-top: .25em;">Last updated {{ props.item.lastModified | prettyDateTimeShort }}</div>
              </td>
            </tr>
          </template>
        </v-data-table>

      </div>

    </div>
  </div>
</template>

<script>
  import AuctionRoleStatusIndicator from './elements/AuctionRoleStatusIndicator.vue'
  import AuctionStatusIndicator from './elements/AuctionStatusIndicator.vue'
  import { auctionRoles, auctionRoleLabel, agencyRelationships, auctionStatuses, auctionStatusLabel } from '../utils/PfxUtils.js'
  import { mapGetters } from 'vuex'

  export default {
    name: 'BiddersPage',
    components: {
      AuctionRoleStatusIndicator,
      AuctionStatusIndicator
    },
    data() {
      return {
        auctionRoles,
        agencyRelationships,
        auctionStatuses,

        auctionId: this.$route.params.auctionId,
        auction: {},
        loadingCounter: 0,
        participants: [],
        headers: [
          { text: 'Bidder Information', align: 'left', value: 'profile.firstName' },
          { text: 'Representation', align: 'left', sortable: false },
          { text: 'Form ID', align: 'left', value: 'id' },
          { text: 'Applied', align: 'left', value: 'created' },
          { text: 'Bids', align: 'left', value: 'created' },
          { text: 'Status', align: 'left', value: 'role' },
        ],
        pagination: {
          sortBy: 'created', descending: true, rowsPerPage: 100000000
        },
        message: '',
        listingAgentProfile: {},
        bids: []
      }
    },
    computed: {
      ...mapGetters([
        'subdomain'
      ]),

      propertyPhoto() {
        if (this.auction.metadata && this.auction.metadata.photos && this.auction.metadata.photos.length > 0) {
          return this.auction.metadata.photos[0]
        }
        return require('../assets/placeholder-photos.jpg')
      }
    },
    created() {
      this.loadData()
    },
    methods: {
      auctionRoleLabel,
      auctionStatusLabel,

      loadData() {
        this.participants.splice(0) // clear the array before (re)loading
        const that = this
        that.$store.commit('setLoadingSpinner', true)
        this.$PfxBackendApi.getAuction(this.auctionId).then(res => {
          console.log('getAuction res', res)
          this.auction = res.data || {}

          this.$PfxBackendApi.getUser(that.auction.listingAgent).then(res => {
            console.log('getUser (listing agent) res', res)
            that.listingAgentProfile = res.data || {}
          }).catch(err => {
            console.error('getUser (listing agent) err', err)
            this.$notify({ type: 'danger', message: 'Cannot fetch listing agent profile' })
          })

          if (this.auction.status === auctionStatuses.AUCTION.value ||
            this.auction.status === auctionStatuses.PROCESSING.value ||
            this.auction.status === auctionStatuses.IN_ESCROW.value ||
            this.auction.status === auctionStatuses.SOLD.value ||
            this.auction.status === auctionStatuses.CANCELLED.value ||
            this.auction.status === auctionStatuses.CLOSED.value) {
            this.$PfxBackendApi.getBidsForAuction(this.subdomain, this.auctionId)
              .then(res => {
                console.log('getBidsForAuction res', res)
                const bids = res.data || []
                bids.sort((a,b) => a.amount < b.amount ? 1 : a.amount === b.amount ? 0 : -1)
                this.bids = bids
              })
              .catch(err => console.error('getBidsForAuction err', err))
          } else {
            console.log('not loading bids since status [%s]', this.auction.status)
          }

          this.$PfxBackendApi.getAuctionParticipants(this.subdomain, this.auctionId).then(res => {
            console.log('getAuctionParticipants res', res)
            const participants = res.data || []
            that.loadingCounter = participants.length
            participants
              .filter(participant => {
                const valid = participant.role === auctionRoles.APPLICANT.value ||
                  participant.role === auctionRoles.BUYER.value ||
                  participant.role === auctionRoles.WINNER.value ||
                  participant.role === auctionRoles.ABANDON.value ||
                  participant.role === auctionRoles.DISQUALIFIED.value
                if (!valid) that.loadingCounter--
                return valid
              })
              .forEach(participant => {
                participant.profile = {}
                participant.sellingAgentProfile = {}
                participant.listingAgentProfile = {}
                that.participants.push(participant)

                this.$PfxBackendApi.getUser(participant.userId).then(res => {
                  console.info('getUser (buyer) res', res)
                  if (res.data.id) {
                    participant.profile = res.data
                  }
                }).catch(err => {
                  console.error('getUser (buyer) error', err)
                  this.$notify({ type: 'danger', message: 'Cannot fetch buyer profile' })
                }).then(() => {
                  that.loadingCounter--
                })

                if (participant.agencyRelationship === agencyRelationships.SELLING_AGENT.value) {
                  this.$PfxBackendApi.getAuctionSellingAgent(this.subdomain, this.auctionId, participant.id).then(res => {
                    console.info('getAuctionSellingAgent res', res)
                    const agentRole = (res.data && res.data.sellingAgentRole) || {}
                    if (agentRole.role === auctionRoles.SELLING_AGENT.value && agentRole.userId) {
                      this.$PfxBackendApi.getUser(agentRole.userId).then(res => {
                        console.info('getUser (selling agent) res', res)
                        participant.sellingAgentProfile = res.data || {}
                      }).catch(err => {
                        console.error('getUser (selling agent) err', err)
                        this.$notify({type: 'danger', message: 'Cannot fetch selling agent profile'})
                      })
                    }
                  }).catch(err => {
                    console.error('getAuctionSellingAgent err', err)
                    //this.$notify({type: 'danger', message: 'Cannot fetch data about selling agent'})
                  })
                }
              })
            that.$store.commit('setLoadingSpinner', false)
          }).catch(err => {
            console.error('getAuctionParticipants error', err)
            that.$store.commit('setLoadingSpinner', false)
            this.$notify({ type: 'danger', message: 'Cannot fetch data about bidders' })
            that.loadingCounter = 0
          })

        }).catch(err => {
          console.error('getAuction error', err)
          this.$notify({ type: 'danger', message: 'Cannot fetch data about auction' })
        })
      },

      clickBidder(auctionRole) {
        if (auctionRole.role === auctionRoles.WINNER.value) {
          this.$router.push('/closing/' + this.auctionId)
        } else {
          this.$router.push('/registration-form/' + this.auctionId + '/' + auctionRole.id)
        }
      },

      myHighestBid(userId) {
        const bid = this.bids.find(bid => bid.userId === userId) // note: this.bids is already sorted by amount (highest first)
        return (bid && bid.amount) || 0
      }

    }
  }
</script>

<style lang="scss" scoped>
  @import 'node_modules/jeet/scss/index';

  h2 {
    font-family: 'Encode Sans', sans-serif;
    font-size: 24px;
    font-weight: 300;
    margin: 0 0 1em;
    line-height: 24px;
  }

  .user-avatar {
    max-height: 24px;
    float: left;
    margin: 0.5em 0.5em 0.5em 0;
  }

  small {
    color: #999;
  }

  .auction-container {
    @include clearfix();
    margin: 0 0 1em;

    .auction-details {
      @include column(3/7);

      .auction-photo {
        float: left;
        margin-right: 1rem;
        width: 150px;
        height: 100px;
        background-size: cover;
        background-position: center;
      }

      font-size: 12px;
      strong {
        font-family: 'Encode Sans', sans-serif;
        font-size: 20px;
        font-weight: 300;
      }
    }
    .auction-reg {
      @include column(2/7);
      font-size: 0.8em;
    }
    .auction-times {
      @include column(2/7);
      font-size: 0.8em;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/BiddersPage.vue